/**
 * Estimating's cross-page composites: work stages, product types, both price
 * books (a contractor's cost list and the company's own sell list share one
 * panel, parameterized by `side`), the quick-compare tool, and the package
 * table the comparison renders as. Used by Settings (stages, product types,
 * the sell book), ContractorDetail (a contractor's cost book), and the
 * Estimates pages. See docs/estimating.md.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { api, apiErrorMessage, fmtAmount, formatPercent, unwrap } from "../api";
import { Button, Empty, ErrorNote, Field, Loading, Panel, Table, Td, Th, input } from "../ui";
import { useResource } from "../useResource";

export const PRICING_UNITS = [
  "inch",
  "linear_foot",
  "square_foot",
  "each",
  "hour",
  "day",
  "flat",
] as const;
export type PricingUnit = (typeof PRICING_UNITS)[number];

export const UNIT_LABELS: Record<PricingUnit, string> = {
  inch: "inch",
  linear_foot: "linear foot",
  square_foot: "square foot",
  each: "each",
  hour: "hour",
  day: "day",
  flat: "flat",
};

export function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

/* ---------------------------------------------------------------- *
 * Work stages
 * ---------------------------------------------------------------- */

export function useWorkStages(slug: string) {
  return useQuery({
    queryKey: ["work-stages", slug],
    queryFn: async () => unwrap(await api.api.c[":slug"].pricing.stages.$get({ param: { slug } })),
  });
}

export function WorkStagesPanel({ slug, role }: { slug: string; role: string }) {
  const canManage = role === "manager" || role === "admin" || role === "owner";
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const stages = useWorkStages(slug);

  const r = useResource({
    key: ["work-stages", slug],
    list: async () => unwrap(await api.api.c[":slug"].pricing.stages.$get({ param: { slug } })),
    create: async (body: { code: string; name: string }) =>
      unwrap(await api.api.c[":slug"].pricing.stages.$post({ param: { slug }, json: body })),
  });

  return (
    <Panel
      title="Work stages"
      action={
        canManage && <Button onClick={() => setOpen(!open)}>{open ? "Cancel" : "New stage"}</Button>
      }
    >
      <p className="mb-3 max-w-prose text-sm text-neutral-500">
        The pieces of work a price can cover — fabrication, install, delivery. A price is only
        comparable to another once both say which of these they buy.
      </p>
      {open && (
        <form
          className="mb-4 flex items-end gap-2"
          onSubmit={async (e) => {
            e.preventDefault();
            await r.create({ code: code.trim(), name: name.trim() });
            setCode("");
            setName("");
            setOpen(false);
          }}
        >
          {r.formError && <ErrorNote>{r.formError}</ErrorNote>}
          <Field label="Code" error={r.fieldErrors.code}>
            <input
              className={input}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
            />
          </Field>
          <Field label="Name" error={r.fieldErrors.name}>
            <input
              className={input}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </Field>
          <Button type="submit" disabled={r.saving}>
            Add
          </Button>
        </form>
      )}
      {stages.isPending ? (
        <Loading />
      ) : (stages.data?.stages.length ?? 0) === 0 ? (
        <Empty title="No work stages yet">Add the stages your jobs are built from.</Empty>
      ) : (
        <div className="flex flex-wrap gap-2">
          {stages.data!.stages.map((s) => (
            <span
              key={s.id}
              className="rounded-full border border-neutral-200 bg-neutral-50 px-3 py-1 text-sm text-neutral-700"
            >
              {s.name}
            </span>
          ))}
        </div>
      )}
    </Panel>
  );
}

/* ---------------------------------------------------------------- *
 * Product types
 * ---------------------------------------------------------------- */

export function useProductTypes(slug: string) {
  return useQuery({
    queryKey: ["product-types", slug],
    queryFn: async () =>
      unwrap(await api.api.c[":slug"].pricing["product-types"].$get({ param: { slug } })),
  });
}

export function ProductTypesPanel({ slug, role }: { slug: string; role: string }) {
  const canManage = role === "manager" || role === "admin" || role === "owner";
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [stageIds, setStageIds] = useState<string[]>([]);

  const types = useProductTypes(slug);
  const stages = useWorkStages(slug);

  const r = useResource({
    key: ["product-types", slug],
    list: async () =>
      unwrap(await api.api.c[":slug"].pricing["product-types"].$get({ param: { slug } })),
    create: async (body: { code: string; name: string }) =>
      unwrap(
        await api.api.c[":slug"].pricing["product-types"].$post({ param: { slug }, json: body }),
      ),
  });

  const setStages = useMutation({
    mutationFn: async ({ id, stageIds: ids }: { id: string; stageIds: string[] }) =>
      unwrap(
        await api.api.c[":slug"].pricing["product-types"][":id"].stages.$put({
          param: { slug, id },
          json: { stageIds: ids },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["product-types", slug] }),
  });

  return (
    <Panel
      title="Product types"
      action={
        canManage && (
          <Button onClick={() => setOpen(!open)}>{open ? "Cancel" : "New product type"}</Button>
        )
      }
    >
      <p className="mb-3 max-w-prose text-sm text-neutral-500">
        What's being priced, and the stages it normally needs. A new estimate line starts from
        these; the stages it actually needs stay editable per line.
      </p>
      {open && (
        <form
          className="mb-4 rounded-xl border border-neutral-200 bg-white p-4 shadow-sm"
          onSubmit={async (e) => {
            e.preventDefault();
            const created = await r.create({ code: code.trim(), name: name.trim() });
            if (stageIds.length > 0) {
              await setStages.mutateAsync({
                id: (created as { productType: { id: string } }).productType.id,
                stageIds,
              });
            }
            setCode("");
            setName("");
            setStageIds([]);
            setOpen(false);
          }}
        >
          {r.formError && <ErrorNote>{r.formError}</ErrorNote>}
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Code" error={r.fieldErrors.code}>
              <input
                className={input}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
              />
            </Field>
            <Field label="Name" error={r.fieldErrors.name}>
              <input
                className={input}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </Field>
          </div>
          <Field label="Stages it normally needs">
            <div className="flex flex-wrap gap-2 pt-1">
              {(stages.data?.stages ?? []).map((s) => (
                <label
                  key={s.id}
                  className="flex items-center gap-1.5 rounded-full border border-neutral-200 px-2.5 py-1 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={stageIds.includes(s.id)}
                    onChange={(e) =>
                      setStageIds(
                        e.target.checked ? [...stageIds, s.id] : stageIds.filter((x) => x !== s.id),
                      )
                    }
                  />
                  {s.name}
                </label>
              ))}
            </div>
          </Field>
          <Button type="submit" disabled={r.saving} className="mt-3">
            Add product type
          </Button>
        </form>
      )}
      {types.isPending ? (
        <Loading />
      ) : (types.data?.productTypes.length ?? 0) === 0 ? (
        <Empty title="No product types yet" />
      ) : (
        <Table
          head={
            <>
              <Th>Code</Th>
              <Th>Name</Th>
              <Th>Stages</Th>
            </>
          }
        >
          {types.data!.productTypes.map((t) => (
            <tr key={t.id} className="border-b border-neutral-100 even:bg-neutral-50">
              <Td mono muted>
                {t.code}
              </Td>
              <Td>{t.name}</Td>
              <Td muted>
                {t.stageIds
                  .map((id) => stages.data?.stages.find((s) => s.id === id)?.name)
                  .filter(Boolean)
                  .join(", ") || "—"}
              </Td>
            </tr>
          ))}
        </Table>
      )}
    </Panel>
  );
}

/* ---------------------------------------------------------------- *
 * Price books — cost (a contractor's) and sell (the company's own)
 * ---------------------------------------------------------------- */

type PriceItemRow = {
  key: number;
  label: string;
  productTypeId: string;
  costCodeId: string;
  unit: PricingUnit;
  unitPrice: string;
  minCharge: string;
  bundleKey: string;
  stageIds: string[];
};

export function PriceBookPanel({
  slug,
  role,
  side,
  contractorId,
  currency,
}: {
  slug: string;
  role: string;
  side: "cost" | "sell";
  /** Required for `side: "cost"` — whose price list this is. */
  contractorId?: string;
  currency: string;
}) {
  const needed = side === "sell" ? "admin" : "manager";
  const rank: Record<string, number> = { viewer: 0, accountant: 1, manager: 2, admin: 3, owner: 4 };
  const canManage = (rank[role] ?? 0) >= (rank[needed] ?? 99);

  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newFrom, setNewFrom] = useState(todayIso());
  const [openListId, setOpenListId] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const productTypes = useProductTypes(slug);
  const stages = useWorkStages(slug);

  const lists = useQuery({
    queryKey: ["price-lists", slug, side, contractorId ?? null],
    queryFn: async () =>
      unwrap(
        await api.api.c[":slug"].pricing["price-lists"].$get({
          param: { slug },
          query: { side, ...(contractorId ? { contractorId } : {}) },
        }),
      ),
  });

  const createList = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.api.c[":slug"].pricing["price-lists"].$post({
          param: { slug },
          json: {
            side,
            ...(contractorId ? { contractorId } : {}),
            name: newName.trim(),
            currency,
            effectiveFrom: newFrom,
          },
        }),
      ),
    onSuccess: async (res) => {
      setNewName("");
      setCreating(false);
      setFormError(null);
      await qc.invalidateQueries({ queryKey: ["price-lists", slug] });
      setOpenListId((res as { priceList: { id: string } }).priceList.id);
    },
    onError: (err) => setFormError(apiErrorMessage(err, "Couldn't create that price list.")),
  });

  const activateList = useMutation({
    mutationFn: async (id: string) =>
      unwrap(
        await api.api.c[":slug"].pricing["price-lists"][":id"].$patch({
          param: { slug, id },
          json: { status: "active" },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["price-lists", slug] }),
    onError: (err) => setFormError(apiErrorMessage(err, "Couldn't activate that price list.")),
  });

  return (
    <Panel
      title={side === "sell" ? "Price book — what we charge" : "Price list"}
      action={
        canManage && (
          <Button onClick={() => setCreating(!creating)}>
            {creating ? "Cancel" : "New price list"}
          </Button>
        )
      }
    >
      {side === "sell" && (
        <p className="mb-3 max-w-prose text-sm text-neutral-500">
          Your own rates — what a line prices off when its basis is "price book". Writing this needs
          the admin role; what a contractor charges you is a manager's call, what you charge a
          client is the business.
        </p>
      )}
      {formError && <ErrorNote>{formError}</ErrorNote>}
      {creating && (
        <form
          className="mb-4 flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            createList.mutate();
          }}
        >
          <Field label="Name">
            <input
              className={input}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              required
            />
          </Field>
          <Field label="Effective from">
            <input
              type="date"
              className={input}
              value={newFrom}
              onChange={(e) => setNewFrom(e.target.value)}
              required
            />
          </Field>
          <Button type="submit" disabled={createList.isPending}>
            Create
          </Button>
        </form>
      )}

      {lists.isPending ? (
        <Loading />
      ) : (lists.data?.priceLists.length ?? 0) === 0 ? (
        <Empty title="No price lists yet" />
      ) : (
        <div className="flex flex-col gap-3">
          {lists.data!.priceLists.map((l) => (
            <div key={l.id} className="rounded-xl border border-neutral-200 bg-white shadow-sm">
              <button
                type="button"
                className="flex w-full items-center justify-between px-4 py-3 text-left"
                onClick={() => setOpenListId(openListId === l.id ? null : l.id)}
              >
                <span>
                  <span className="font-medium text-neutral-900">{l.name}</span>
                  <span
                    className={`ml-2 text-xs ${l.status === "draft" ? "font-medium text-amber-700" : "text-neutral-500"}`}
                  >
                    {l.status === "draft" ? "draft — not yet used in comparisons" : l.status} · from{" "}
                    {l.effectiveFrom}
                    {l.effectiveTo ? ` to ${l.effectiveTo}` : ""} · {l.items.length}{" "}
                    {l.items.length === 1 ? "price" : "prices"}
                  </span>
                </span>
                <span className="text-neutral-400">{openListId === l.id ? "▲" : "▼"}</span>
              </button>
              {openListId === l.id && (
                <div className="border-t border-neutral-100 px-4 py-4">
                  {l.status === "draft" && (
                    <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                      <span>
                        This list is still a draft — its prices won't show up in any comparison
                        until you activate it.
                      </span>
                      {canManage && (
                        <Button
                          onClick={() => activateList.mutate(l.id)}
                          disabled={activateList.isPending}
                        >
                          {activateList.isPending ? "Activating…" : "Activate"}
                        </Button>
                      )}
                    </div>
                  )}
                  <PriceItemsEditor
                    slug={slug}
                    priceListId={l.id}
                    currency={l.currency}
                    items={l.items}
                    productTypes={productTypes.data?.productTypes ?? []}
                    stages={stages.data?.stages ?? []}
                    canManage={canManage}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

function PriceItemsEditor({
  slug,
  priceListId,
  currency,
  items,
  productTypes,
  stages,
  canManage,
}: {
  slug: string;
  priceListId: string;
  currency: string;
  items: readonly {
    id: string;
    label: string;
    productTypeId: string;
    costCodeId: string | null;
    unit: string;
    unitPrice: string;
    minCharge: string | null;
    bundleKey: string | null;
    stageIds: readonly string[];
  }[];
  productTypes: readonly { id: string; name: string }[];
  stages: readonly { id: string; name: string }[];
  canManage: boolean;
}) {
  const qc = useQueryClient();
  const nextKey = useRef(0);
  const [rows, setRows] = useState<PriceItemRow[]>(() =>
    items.map((i) => ({
      key: nextKey.current++,
      label: i.label,
      productTypeId: i.productTypeId,
      costCodeId: i.costCodeId ?? "",
      unit: i.unit as PricingUnit,
      unitPrice: i.unitPrice,
      minCharge: i.minCharge ?? "",
      // Server namespaces bundle keys as `${priceListId}:${key}` — show the
      // plain suffix so it round-trips through the form unchanged.
      bundleKey: i.bundleKey?.split(":").slice(1).join(":") ?? "",
      stageIds: [...i.stageIds],
    })),
  );
  const [error, setError] = useState<string | null>(null);

  const costCodes = useQuery({
    queryKey: ["cost-codes", slug],
    queryFn: async () => unwrap(await api.api.c[":slug"]["cost-codes"].$get({ param: { slug } })),
  });

  const save = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.api.c[":slug"].pricing["price-lists"][":id"].items.$put({
          param: { slug, id: priceListId },
          json: {
            items: rows.map((r) => ({
              label: r.label,
              productTypeId: r.productTypeId,
              ...(r.costCodeId ? { costCodeId: r.costCodeId } : {}),
              unit: r.unit,
              unitPrice: r.unitPrice,
              ...(r.minCharge ? { minCharge: r.minCharge } : {}),
              ...(r.bundleKey ? { bundleKey: r.bundleKey } : {}),
              stageIds: r.stageIds,
            })),
          },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["price-lists", slug] }),
    onError: (err) => setError(apiErrorMessage(err, "Couldn't save these prices.")),
  });

  function update(key: number, patch: Partial<PriceItemRow>) {
    setRows(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  return (
    <div>
      {error && <ErrorNote>{error}</ErrorNote>}
      <div className="flex flex-col gap-3">
        {rows.map((r) => (
          <div key={r.key} className="rounded-lg border border-neutral-200 p-3">
            <div className="grid gap-2 sm:grid-cols-6">
              <Field label="Label">
                <input
                  className={input}
                  value={r.label}
                  onChange={(e) => update(r.key, { label: e.target.value })}
                  disabled={!canManage}
                />
              </Field>
              <Field label="Product type">
                <select
                  className={input}
                  value={r.productTypeId}
                  onChange={(e) => update(r.key, { productTypeId: e.target.value })}
                  disabled={!canManage}
                >
                  <option value="">— pick —</option>
                  {productTypes.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Cost code" hint="Optional — falls back to the product's default">
                <select
                  className={input}
                  value={r.costCodeId}
                  onChange={(e) => update(r.key, { costCodeId: e.target.value })}
                  disabled={!canManage}
                >
                  <option value="">— default —</option>
                  {(costCodes.data?.costCodes ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.code} {c.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Unit">
                <select
                  className={input}
                  value={r.unit}
                  onChange={(e) => update(r.key, { unit: e.target.value as PricingUnit })}
                  disabled={!canManage}
                >
                  {PRICING_UNITS.map((u) => (
                    <option key={u} value={u}>
                      {UNIT_LABELS[u]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={`Rate (${currency}/${UNIT_LABELS[r.unit]})`}>
                <input
                  className={input}
                  value={r.unitPrice}
                  onChange={(e) => update(r.key, { unitPrice: e.target.value })}
                  disabled={!canManage}
                />
              </Field>
              <Field label="Minimum charge" hint="Optional">
                <input
                  className={input}
                  value={r.minCharge}
                  onChange={(e) => update(r.key, { minCharge: e.target.value })}
                  disabled={!canManage || r.unit === "flat"}
                />
              </Field>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <span className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                Covers
              </span>
              {stages.map((s) => (
                <label key={s.id} className="flex items-center gap-1 text-sm">
                  <input
                    type="checkbox"
                    checked={r.stageIds.includes(s.id)}
                    disabled={!canManage}
                    onChange={(e) =>
                      update(r.key, {
                        stageIds: e.target.checked
                          ? [...r.stageIds, s.id]
                          : r.stageIds.filter((x) => x !== s.id),
                      })
                    }
                  />
                  {s.name}
                </label>
              ))}
              <Field label="Bundle key" hint="Optional — shares a rate with another item">
                <input
                  className={input}
                  value={r.bundleKey}
                  onChange={(e) => update(r.key, { bundleKey: e.target.value })}
                  disabled={!canManage}
                />
              </Field>
              {canManage && (
                <Button
                  variant="ghost"
                  onClick={() => setRows(rows.filter((x) => x.key !== r.key))}
                >
                  Remove
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
      {canManage && (
        <div className="mt-3 flex gap-2">
          <Button
            variant="ghost"
            onClick={() =>
              setRows([
                ...rows,
                {
                  key: nextKey.current++,
                  label: "",
                  productTypeId: "",
                  costCodeId: "",
                  unit: "inch",
                  unitPrice: "0.0000",
                  minCharge: "",
                  bundleKey: "",
                  stageIds: [],
                },
              ])
            }
          >
            Add price
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Save prices"}
          </Button>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- *
 * The comparison itself
 * ---------------------------------------------------------------- */

export interface PricedItem {
  id: string;
  contractorId: string;
  contractorName: string;
  label: string;
  unit: string;
  unitPrice: string;
  amount: string;
  minChargeApplied: boolean;
  coversStageIds: readonly string[];
  surplusStageIds: readonly string[];
}

export interface BidPackage {
  items: readonly PricedItem[];
  total: string;
  missingStageIds: readonly string[];
  contractorCount: number;
}

export interface BidComparison {
  packages: readonly BidPackage[];
  excluded: readonly { candidateId: string; reason: string }[];
  truncated: boolean;
}

function stageName(id: string, stages: readonly { id: string; name: string }[]) {
  return stages.find((s) => s.id === id)?.name ?? id;
}

export function PackageTable({
  comparison,
  currency,
  stages,
  selectedIds,
  onChoose,
}: {
  comparison: BidComparison;
  currency: string;
  stages: readonly { id: string; name: string }[];
  /** Highlights the package(s) matching the estimate's current selection. */
  selectedIds?: readonly string[] | undefined;
  /** Omit for a read-only comparison (the quick-compare tool). */
  onChoose?: ((priceItemIds: string[]) => void) | undefined;
}) {
  if (comparison.packages.length === 0) {
    return (
      <Empty title="No valid way to buy this yet">
        {comparison.excluded.length > 0
          ? "Every price on file was excluded — see why below."
          : "No contractor has quoted a price covering any of these stages."}
      </Empty>
    );
  }

  return (
    <div>
      <div className="flex flex-col gap-2">
        {comparison.packages.map((pkg) => {
          const ids = pkg.items.map((it) => it.id).sort();
          const isSelected =
            selectedIds &&
            selectedIds.length === ids.length &&
            [...selectedIds].sort().every((id, j) => id === ids[j]);
          return (
            <div
              key={ids.join(",")}
              className={`rounded-lg border p-3 ${
                pkg.missingStageIds.length === 0
                  ? "border-neutral-200 bg-white"
                  : "border-amber-200 bg-amber-50/40"
              } ${isSelected ? "ring-2 ring-accent-500" : ""}`}
            >
              <div className="flex items-center justify-between">
                <div className="text-sm">
                  {pkg.items.map((it) => `${it.contractorName} — ${it.label}`).join("  +  ")}
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-medium">{fmtAmount(pkg.total, currency)}</span>
                  {onChoose && (
                    <Button variant="ghost" onClick={() => onChoose(pkg.items.map((it) => it.id))}>
                      {isSelected ? "Chosen" : "Choose"}
                    </Button>
                  )}
                </div>
              </div>
              {pkg.missingStageIds.length > 0 && (
                <p className="mt-1 text-xs text-amber-700">
                  Missing: {pkg.missingStageIds.map((id) => stageName(id, stages)).join(", ")}
                </p>
              )}
              {pkg.items.some((it) => it.surplusStageIds.length > 0) && (
                <p className="mt-1 text-xs text-neutral-500">
                  Also covers:{" "}
                  {pkg.items
                    .flatMap((it) => it.surplusStageIds)
                    .map((id) => stageName(id, stages))
                    .join(", ")}
                </p>
              )}
            </div>
          );
        })}
      </div>
      {comparison.truncated && (
        <p className="mt-2 text-xs text-neutral-500">
          There were more valid combinations than fit here — narrow the stages or measurements to
          see the rest.
        </p>
      )}
      {comparison.excluded.length > 0 && (
        <details className="mt-3 text-sm text-neutral-500">
          <summary className="cursor-pointer">
            Why isn't everything here? ({comparison.excluded.length})
          </summary>
          <ul className="mt-1 list-disc pl-5">
            {comparison.excluded.map((e) => (
              <li key={e.candidateId}>{e.reason}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

/** The "just show me the numbers" tool: pick the stages this job needs, enter
 *  what was measured, and see every valid way to buy it — cheapest first.
 *  Nothing here is saved; see EstimateDetail for the version that is. */
export function QuickCompare({ slug, currency }: { slug: string; currency: string }) {
  const stages = useWorkStages(slug);
  const [stageIds, setStageIds] = useState<string[]>([]);
  const nextKey = useRef(0);
  const [measures, setMeasures] = useState<{ key: number; unit: PricingUnit; quantity: string }[]>(
    () => [{ key: nextKey.current++, unit: "inch", quantity: "" }],
  );
  const [pricedOn, setPricedOn] = useState(todayIso());
  const [result, setResult] = useState<BidComparison | null>(null);
  const [error, setError] = useState<string | null>(null);

  const compare = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.api.c[":slug"].estimates.compare.$post({
          param: { slug },
          json: {
            currency,
            pricedOn,
            requiredStageIds: stageIds,
            measures: measures.filter((m) => m.quantity.trim() !== ""),
          },
        }),
      ),
    onSuccess: (res) => {
      setError(null);
      setResult((res as { comparison: BidComparison }).comparison);
    },
    onError: (err) => {
      setResult(null);
      setError(apiErrorMessage(err, "Couldn't run that comparison."));
    },
  });

  return (
    <Panel title="Quick compare">
      <p className="mb-3 max-w-prose text-sm text-neutral-500">
        What would this cost, from everyone who's quoted a price? Nothing here is saved — open an
        estimate to keep it.
      </p>
      <form
        className="mb-4 flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          compare.mutate();
        }}
      >
        <Field label="Stages this job needs">
          <div className="flex flex-wrap gap-2 pt-1">
            {(stages.data?.stages ?? []).map((s) => (
              <label
                key={s.id}
                className="flex items-center gap-1.5 rounded-full border border-neutral-200 px-2.5 py-1 text-sm"
              >
                <input
                  type="checkbox"
                  checked={stageIds.includes(s.id)}
                  onChange={(e) =>
                    setStageIds(
                      e.target.checked ? [...stageIds, s.id] : stageIds.filter((x) => x !== s.id),
                    )
                  }
                />
                {s.name}
              </label>
            ))}
          </div>
        </Field>
        <div className="flex flex-col gap-2">
          {measures.map((m) => (
            <div key={m.key} className="flex items-end gap-2">
              <Field label="Unit">
                <select
                  className={input}
                  value={m.unit}
                  onChange={(e) =>
                    setMeasures(
                      measures.map((x) =>
                        x.key === m.key ? { ...x, unit: e.target.value as PricingUnit } : x,
                      ),
                    )
                  }
                >
                  {PRICING_UNITS.map((u) => (
                    <option key={u} value={u}>
                      {UNIT_LABELS[u]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Quantity">
                <input
                  className={input}
                  value={m.quantity}
                  onChange={(e) =>
                    setMeasures(
                      measures.map((x) =>
                        x.key === m.key ? { ...x, quantity: e.target.value } : x,
                      ),
                    )
                  }
                />
              </Field>
              <Button
                variant="ghost"
                onClick={() => setMeasures(measures.filter((x) => x.key !== m.key))}
              >
                Remove
              </Button>
            </div>
          ))}
          <Button
            variant="ghost"
            onClick={() =>
              setMeasures([...measures, { key: nextKey.current++, unit: "inch", quantity: "" }])
            }
          >
            Add a measurement
          </Button>
        </div>
        <Field label="Priced as of">
          <input
            type="date"
            className={input}
            value={pricedOn}
            onChange={(e) => setPricedOn(e.target.value)}
          />
        </Field>
        <div>
          <Button type="submit" disabled={compare.isPending || stageIds.length === 0}>
            {compare.isPending ? "Comparing…" : "Compare"}
          </Button>
        </div>
      </form>
      {error && <ErrorNote>{error}</ErrorNote>}
      {result && (
        <PackageTable comparison={result} currency={currency} stages={stages.data?.stages ?? []} />
      )}
    </Panel>
  );
}
